import type * as ozon from "@/api/ozonSupply";

/** Тип пункта сдачи словами менеджера. Ключи — форма черновика площадки (без `WAREHOUSE_TYPE_`). */
export const DROP_OFF_TYPE_RU: Record<string, string> = {
  DELIVERY_POINT: "ПВЗ",
  ORDERS_RECEIVING_POINT: "пункт приёма",
  SORTING_CENTER: "сорт. центр",
  CROSS_DOCK: "кросс-док",
  FULL_FILLMENT: "склад",
};

/** Незнакомый тип показываем КАК ЕСТЬ: площадка добавит новый — увидим её слово, а не пустоту. */
export function dropOffTypeRu(type: string): string {
  return DROP_OFF_TYPE_RU[type] ?? type;
}

/** Точки, куда сдаём КОРОБАМИ (как `BOX_DROP_OFF_TYPES` бэкенда): у них справка про лимиты. */
export const BOX_DROP_OFF_TYPES = new Set([
  "DELIVERY_POINT",
  "ORDERS_RECEIVING_POINT",
]);

/** Значение пункта «Другой пункт / ПВЗ…» в списке: не id точки, а «открыть поиск». */
export const SEARCH_OPTION = "search";

/** Выбранный пункт так, как его показывать. */
export interface ShownDropOff {
  id: number;
  /** null — имени не знает никто: не записано (выбран до 12.10) и в списке `/drop-offs` его нет */
  name: string | null;
  /** тип из записи направления; null — у старых записей, выбранных до записи типа */
  type: string | null;
}

/** Что выбрано на направлении: имя из записи, иначе из списка `/drop-offs` по id (старые записи
 *  имени не хранят). Пусто `drop_off_id` — ничего не выбрано («повезём как обычно»). */
export function shownDropOff(
  d: Pick<
    ozon.OzonDirection,
    "drop_off_id" | "drop_off_name" | "drop_off_type"
  >,
  dropOffs: ozon.OzonDropOff[],
): ShownDropOff | null {
  if (d.drop_off_id == null) return null;
  const name =
    d.drop_off_name ??
    dropOffs.find((p) => p.id === d.drop_off_id)?.name ??
    null;
  return { id: d.drop_off_id, name, type: d.drop_off_type };
}

/** Подпись точки: имя, а без имени — номер (не пустое место: выбор есть, просто безымянный). */
export function dropOffLabel(p: ShownDropOff): string {
  return p.name ?? `пункт #${p.id}`;
}

/** Бейдж типа нужен, только если точка НЕ обычный кросс-док (его в списке — все). */
export function dropOffBadge(type: string | null): string | null {
  return type && type !== "CROSS_DOCK" ? dropOffTypeRu(type) : null;
}

/** Опция выпадающего списка пунктов сдачи. */
export interface DropOffOption {
  id: number;
  label: string;
  /** имя для `POST …/kind`: у точки НЕ из списка (ПВЗ из поиска) оно обязательно */
  name: string | null;
}

/** Опции списка пунктов: `/drop-offs` плюс выбранная точка, если её там нет (ПВЗ из поиска —
 *  в списке площадки его нет никогда). Без этой опции `<select>` показал бы первый пункт списка,
 *  то есть соврал бы, через какую точку везём. */
export function dropOffOptions(
  d: Pick<
    ozon.OzonDirection,
    "drop_off_id" | "drop_off_name" | "drop_off_type"
  >,
  dropOffs: ozon.OzonDropOff[],
): DropOffOption[] {
  const list = dropOffs.map((p) => ({ id: p.id, label: p.name, name: p.name }));
  const picked = shownDropOff(d, dropOffs);
  if (picked == null || dropOffs.some((p) => p.id === picked.id)) return list;
  const badge = dropOffBadge(picked.type);
  return [
    {
      id: picked.id,
      label: `${dropOffLabel(picked)}${badge ? ` · ${badge}` : ""}`,
      name: picked.name,
    },
    ...list,
  ];
}

/** Состояния, в которых предложения складов — ответ на ТЕКУЩИЙ черновик. У сорвавшегося
 *  (`failed`) бэкенд оставляет предложения прошлой удачной попытки, и «объём по Ozon» из них
 *  читался бы как ответ, которого не было. */
const LIVE_OFFER_STATES = new Set(["drafted", "ordering", "ordered"]);

/** Предложения актуальны (ответ на текущий черновик), а не остатки прошлой попытки. */
export function offersAreLive(d: Pick<ozon.OzonDirection, "state">): boolean {
  return LIVE_OFFER_STATES.has(d.state);
}

/** Предложение, которое площадка ВЗЯЛА: склад направления (`warehouse_id`), а у кросс-дока, где
 *  склада хранения нет, — предложение без склада (id 0, так его пишет бэкенд). Нет такого —
 *  null: показывать объём не от чего. */
export function takenOffer(
  d: Pick<ozon.OzonDirection, "state" | "warehouse_id" | "offers">,
): ozon.OzonWarehouseOffer | null {
  if (!offersAreLive(d)) return null;
  const id = d.warehouse_id ?? 0;
  return d.offers.find((o) => o.available && o.warehouse_id === id) ?? null;
}

/** Справка у точки, куда сдаём коробками (ПВЗ, пункт приёма). Чисел-пределов здесь НЕТ намеренно
 *  (решение владельца 12.10.2026): лимиты меняет Ozon, и проверяет их он сам на черновике. */
export function boxPointHint(type: string | null): string | null {
  if (type == null || !BOX_DROP_OFF_TYPES.has(type)) return null;
  return (
    `${dropOffTypeRu(type)}: сдаём коробками; лимиты (кол-во коробов, объём) проверяет Ozon по ` +
    "своим габаритам — если не примет, отказ будет на черновике"
  );
}
